/**
 * FaxTransmissionForm
 *
 * Form for fax transmission: recipient + fax number + cover message.
 * Works with FaxTransmission type from the discriminated union.
 */

import React from 'react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { FormField } from './FormField';
import { inputErrorStyles, inputStyles, textareaStyles } from './styles';
import type { FaxTransmissionFormProps } from './types';
import { formatPhoneNumberForDisplay } from './validation';

const COVER_MESSAGE_MAX_LENGTH = 500;

export const FaxTransmissionForm: React.FC<FaxTransmissionFormProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  errors,
}) => {
  const [faxNumberDisplay, setFaxNumberDisplay] = React.useState(
    value.fax_number || '',
  );

  React.useEffect(() => {
    setFaxNumberDisplay(value.fax_number || '');
  }, [value.fax_number]);

  const handleFaxNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setFaxNumberDisplay(rawValue);
    onChange({ ...value, fax_number: rawValue });
  };

  const handleFaxNumberBlur = () => {
    const formatted = formatPhoneNumberForDisplay(faxNumberDisplay);
    setFaxNumberDisplay(formatted);
    onChange({ ...value, fax_number: formatted });
  };

  return (
    <div className={cn('space-y-2.5', className)}>
      <FormField
        label="Recipient"
        htmlFor="fax-displayName"
        required
        error={errors?.display_name}
      >
        <Input
          id="fax-displayName"
          type="text"
          placeholder="Blue Cross Blue Shield"
          value={value.display_name || ''}
          onChange={(e) => onChange({ ...value, display_name: e.target.value })}
          disabled={disabled}
          className={cn(inputStyles, errors?.display_name && inputErrorStyles)}
        />
      </FormField>

      <FormField
        label="Fax Number"
        htmlFor="fax-number"
        required
        error={errors?.fax_number}
      >
        <Input
          id="fax-number"
          type="tel"
          placeholder="(555) 123-4567"
          value={faxNumberDisplay}
          onChange={handleFaxNumberChange}
          onBlur={handleFaxNumberBlur}
          disabled={disabled}
          className={cn(inputStyles, errors?.fax_number && inputErrorStyles)}
        />
      </FormField>

      <FormField
        label="Cover Message"
        htmlFor="fax-coverMessage"
        error={errors?.cover_message}
        characterCount={{
          current: value.cover_message?.length || 0,
          max: COVER_MESSAGE_MAX_LENGTH,
        }}
        inlineCount
      >
        <Textarea
          id="fax-coverMessage"
          placeholder="Optional cover page message..."
          value={value.cover_message || ''}
          onChange={(e) => {
            if (e.target.value.length <= COVER_MESSAGE_MAX_LENGTH) {
              onChange({
                ...value,
                cover_message: e.target.value || undefined,
              });
            }
          }}
          disabled={disabled}
          className={cn(
            textareaStyles,
            errors?.cover_message && inputErrorStyles,
          )}
          rows={2}
          maxLength={COVER_MESSAGE_MAX_LENGTH}
        />
      </FormField>
    </div>
  );
};
